import type { CSSProperties } from "react";
import type { ThemeTokens } from "@course-authoring/shared-types";

export type ContentBlockStyles = {
  container: CSSProperties;
  card: CSSProperties;
  section: CSSProperties;
  h1: CSSProperties;
  h2: CSSProperties;
  h3: CSSProperties;
  h4: CSSProperties;
  p: CSSProperties;

  // Shared primary action button (Likert/WCS/Policy submit buttons all spread from these)
  actionButton: CSSProperties;
  actionButtonHover: CSSProperties;
  actionButtonDisabled: CSSProperties;

  // Shared secondary action button (Cancel/Back — outlined peer to primary)
  secondaryActionButton: CSSProperties;
  secondaryActionButtonHover: CSSProperties;

  // Stylized callouts
  bannerCallout: CSSProperties;
  sectionTab: CSSProperties;
  displayParagraph: CSSProperties;

  // Media
  mediaBlock: CSSProperties;
  mediaOverlay: CSSProperties;
  playButton: CSSProperties;
  mediaLabel: CSSProperties;

  // Accordion
  accordion: CSSProperties;
  accordionItem: CSSProperties;
  accordionTrigger: CSSProperties;
  accordionTitle: CSSProperties;
  accordionIcon: CSSProperties;
  accordionPanel: CSSProperties;

  // Comparison slider
  comparisonContainer: CSSProperties;
  comparisonHeader: CSSProperties;
  comparisonTitle: CSSProperties;
  comparisonSubtitle: CSSProperties;
  comparisonContentPanel: CSSProperties;
  comparisonOptionTitle: CSSProperties;
  comparisonOptionBody: CSSProperties;
  comparisonOptionImage: CSSProperties;

  // Flip cards
  flipCardContainer: CSSProperties;
  flipCardLabel: CSSProperties;
  flipCardHeader: CSSProperties;
  flipCardTitle: CSSProperties;
  flipCardDescription: CSSProperties;
  flipCardGrid: CSSProperties;
  flipCardScene: CSSProperties;
  flipCardInner: CSSProperties;
  flipCardFace: CSSProperties;
  flipCardFaceBack: CSSProperties;
  flipCardText: CSSProperties;
  flipCardTextBack: CSSProperties;

  // Click Expand
  clickExpandContainer: CSSProperties;
  clickExpandHeader: CSSProperties;
  clickExpandTitle: CSSProperties;
  clickExpandDescription: CSSProperties;
  clickExpandAccordionList: CSSProperties;
  clickExpandAccordionItem: CSSProperties;
  clickExpandAccordionItemExpanded: CSSProperties;
  clickExpandAccordionTrigger: CSSProperties;
  clickExpandAccordionTriggerHover: CSSProperties;
  clickExpandAccordionTriggerActive: CSSProperties;
  clickExpandAccordionTopicTitle: CSSProperties;
  clickExpandAccordionIcon: CSSProperties;
  clickExpandAccordionPanel: CSSProperties;
  clickExpandAccordionPanelContent: CSSProperties;
  clickExpandAccordionImage: CSSProperties;
  clickExpandTabShell: CSSProperties;
  clickExpandTabBar: CSSProperties;
  clickExpandTab: CSSProperties;
  clickExpandTabHover: CSSProperties;
  clickExpandTabActive: CSSProperties;
  clickExpandTabSelectWrapper: CSSProperties;
  clickExpandTabSelect: CSSProperties;
  clickExpandTabSelectIcon: CSSProperties;
  clickExpandTabContent: CSSProperties;
  clickExpandTabContentText: CSSProperties;
  clickExpandTabContentImage: CSSProperties;
  clickExpandCheckmark: CSSProperties;

  // Likert scale
  likertContainer: CSSProperties;
  likertHeader: CSSProperties;
  likertTitle: CSSProperties;
  likertDescription: CSSProperties;
  likertCard: CSSProperties;
  likertMetaRow: CSSProperties;
  likertMetaLabel: CSSProperties;
  likertMetaText: CSSProperties;
  likertOptionsScroller: CSSProperties;
  likertOptionsLayout: CSSProperties;
  likertOptionsGrid: CSSProperties;
  likertAxisRow: CSSProperties;
  likertAxisLabel: CSSProperties;
  likertOptionButton: CSSProperties;
  likertOptionButtonHover: CSSProperties;
  likertOptionButtonSelected: CSSProperties;
  likertOptionNumber: CSSProperties;
  likertOptionNumberSelected: CSSProperties;
  likertOptionTooltip: CSSProperties;
  likertOptionTooltipCaret: CSSProperties;
  likertOptionLabelCell: CSSProperties;
  likertOptionLabel: CSSProperties;
  likertOptionLabelSelected: CSSProperties;
  likertSubmitButton: CSSProperties;
  likertSubmitButtonDisabled: CSSProperties;
  likertResultsAnswerRow: CSSProperties;
  likertResultsAnswerLabel: CSSProperties;
  likertResultsAnswerBadge: CSSProperties;
  likertResultsCard: CSSProperties;
  likertResultsDistribution: CSSProperties;
  likertResultsDistributionHeader: CSSProperties;
  likertResultsDistributionTitle: CSSProperties;
  likertResultsDistributionResponses: CSSProperties;
  likertResultsChartSurface: CSSProperties;
  likertResultsChartTrack: CSSProperties;
  likertResultsChartSegment: CSSProperties;
  likertResultsChartSegmentLabel: CSSProperties;
  likertResultsAxisRow: CSSProperties;
  likertResultsAxisLabel: CSSProperties;
  likertResultsTabBar: CSSProperties;
  likertResultsTab: CSSProperties;
  likertResultsTabActive: CSSProperties;
  likertResultsTabContent: CSSProperties;
  likertResultsTable: CSSProperties;
  likertResultsTableHeader: CSSProperties;
  likertResultsTableHeaderCell: CSSProperties;
  likertResultsTableRow: CSSProperties;
  likertResultsTableCell: CSSProperties;
  likertResultsTableRowLabel: CSSProperties;
  // Small-mobile (≤576px) reflow — AA-173
  likertMobileCard: CSSProperties;
  likertMobileOptionsList: CSSProperties;
  likertMobileOptionRow: CSSProperties;
  likertMobileOptionRowHover: CSSProperties;
  likertMobileOptionRowSelected: CSSProperties;
  likertMobileRadio: CSSProperties;
  likertMobileRadioSelected: CSSProperties;
  likertMobileOptionLabel: CSSProperties;
  likertMobileOptionLabelSelected: CSSProperties;
  likertResultsSelectWrap: CSSProperties;
  likertDetailRowsList: CSSProperties;
  likertDetailRow: CSSProperties;
  likertDetailRowHeader: CSSProperties;
  likertDetailRowLabel: CSSProperties;
  likertDetailRowPercent: CSSProperties;
  likertDetailTrack: CSSProperties;
  likertDetailFill: CSSProperties;
  likertTableMobile: CSSProperties;
  likertTableMobileHeaderCell: CSSProperties;
  likertTableMobileCornerCell: CSSProperties;
  likertTableMobileRowLabel: CSSProperties;
  likertTableMobileCell: CSSProperties;

  // Workplace Color Spectrum
  workplaceSpectrumContainer: CSSProperties;
  workplaceSpectrumHeader: CSSProperties;
  workplaceSpectrumTitle: CSSProperties;
  workplaceSpectrumSubtitle: CSSProperties;
  workplaceSpectrumCard: CSSProperties;
  workplaceSpectrumGaugeArea: CSSProperties;
  workplaceSpectrumGaugeSvg: CSSProperties;
  workplaceSpectrumSegment: CSSProperties;
  workplaceSpectrumLabelButton: CSSProperties;
  workplaceSpectrumLabelButtonMuted: CSSProperties;
  workplaceSpectrumLabelTitle: CSSProperties;
  workplaceSpectrumLabelSubtitle: CSSProperties;
  workplaceSpectrumDefinitionArea: CSSProperties;
  workplaceSpectrumDefinition: CSSProperties;
  workplaceSpectrumSubmitButton: CSSProperties;
  workplaceSpectrumSubmitButtonDisabled: CSSProperties;
  workplaceSpectrumResultsHeaderRow: CSSProperties;
  workplaceSpectrumResultsYourAnswerLabel: CSSProperties;
  workplaceSpectrumResultsYourAnswerPill: CSSProperties;
  workplaceSpectrumResultsYourAnswerPillText: CSSProperties;
  workplaceSpectrumResultsYourAnswerPillTextDark: CSSProperties;
  workplaceSpectrumResultsYourAnswerPillBordered: CSSProperties;
  workplaceSpectrumResultsCard: CSSProperties;
  workplaceSpectrumResultsCorrectAnswerCluster: CSSProperties;
  workplaceSpectrumResultsChartsRow: CSSProperties;
  workplaceSpectrumResultsAnswerTile: CSSProperties;
  workplaceSpectrumResultsAnswerBar: CSSProperties;
  workplaceSpectrumResultsEmoji: CSSProperties;
  workplaceSpectrumResultsAnswerLabel: CSSProperties;
  workplaceSpectrumResultsExplanation: CSSProperties;
  workplaceSpectrumResultsChartCard: CSSProperties;
  workplaceSpectrumResultsChartHeader: CSSProperties;
  workplaceSpectrumResultsChartTitle: CSSProperties;
  workplaceSpectrumResultsChartResponses: CSSProperties;
  workplaceSpectrumResultsChartSvg: CSSProperties;
  workplaceSpectrumResultsLegend: CSSProperties;
  workplaceSpectrumResultsLegendItem: CSSProperties;
  workplaceSpectrumResultsLegendLabel: CSSProperties;
  workplaceSpectrumResultsLegendValue: CSSProperties;

  // FAQ Accordion
  faqAccordionContainer: CSSProperties;
  faqAccordionHeader: CSSProperties;
  faqAccordionTitle: CSSProperties;
  faqAccordionSubtitle: CSSProperties;
  faqAccordionItemsList: CSSProperties;
  faqAccordionItem: CSSProperties;
  faqAccordionQuestionRow: CSSProperties;
  faqAccordionQuestionBox: CSSProperties;
  faqAccordionPersonIcon: CSSProperties;
  faqAccordionQuestionText: CSSProperties;
  faqAccordionChevron: CSSProperties;
  faqAccordionChevronExpanded: CSSProperties;
  faqAccordionAnswerWrapper: CSSProperties;
  faqAccordionAnswerRow: CSSProperties;
  faqAccordionAnswerBox: CSSProperties;
  faqAccordionAnswerText: CSSProperties;
  faqAccordionLightbulbIcon: CSSProperties;

  // Policy Acknowledgement
  policyContainer: CSSProperties;
  policyTitle: CSSProperties;
  policyDescription: CSSProperties;
  policyButtonRow: CSSProperties;
  policyViewButton: CSSProperties;
  policyViewButtonHover: CSSProperties;
  policyViewButtonIcon: CSSProperties;
  policyCheckboxWrapper: CSSProperties;
  policyCheckboxInput: CSSProperties;
  policyCheckboxLabel: CSSProperties;
  policyAcknowledgedWrapper: CSSProperties;
  policyAcknowledgedIcon: CSSProperties;
  policyAcknowledgedText: CSSProperties;
  policyHelperText: CSSProperties;
  policyAcknowledgedMessage: CSSProperties;
  policyModalOverlay: CSSProperties;
  policyModalContent: CSSProperties;
  policyModalHeader: CSSProperties;
  policyModalHeaderTitle: CSSProperties;
  policyModalCloseButton: CSSProperties;
  policyModalIframe: CSSProperties;
  policyModalFooter: CSSProperties;
  policyModalDoneButton: CSSProperties;

  // Electronic Signature
  esigContainer: CSSProperties;
  esigTitle: CSSProperties;
  esigDescription: CSSProperties;
  esigCheckboxContainer: CSSProperties;
  esigCheckboxInput: CSSProperties;
  esigCheckboxLabel: CSSProperties;

  // Link
  linkContainer: CSSProperties;
  linkTitle: CSSProperties;
  linkDescription: CSSProperties;
  linkRow: CSSProperties;
  linkAnchor: CSSProperties;
  linkAnchorHover: CSSProperties;
  linkAnchorIcon: CSSProperties;
};

/**
 * Shared "interaction card" shell — the cardAlt (#F1F1F1) panel with a 3px
 * app-background (#FFFFFF) border, 15px radius, and the lg drop shadow used
 * by interaction blocks (Likert, WCS, Click & Expand, Categorization, Single
 * Choice, Comparison Slider, Thank You, e-signature). Spread this base and
 * keep per-site overrides (padding, radius, background) explicit.
 */
export function interactionCard(tokens: ThemeTokens): CSSProperties {
  return {
    backgroundColor: tokens.backgrounds.cardAlt,
    border: `3px solid ${tokens.backgrounds.app}`,
    borderRadius: tokens.radius.xl,
    boxShadow: tokens.shadow.lg,
  };
}

export function createContentBlockStyles(t: ThemeTokens): ContentBlockStyles {
  const s = t.spacing;

  // Shared base for primary action buttons (Likert / WCS / Policy /
  // Single Choice / Categorization submit). Matches the standard-button spec:
  // bg #23242B, fg #FFFFFF, 30px pill, 10px×40px, 24px/400.
  // Per-block style entries spread these and add layout overrides
  // (min-width, margin, etc.).
  const actionButton: CSSProperties = {
    backgroundColor: t.buttons.action.bg,
    color: t.text.onInverse,
    border: 0,
    borderRadius: t.radius.full,
    fontSize: '24px',
    fontWeight: t.typography.weights.regular,
    padding: '10px 40px',
    fontFamily: t.typography.fontFamily,
    cursor: 'pointer',
  };
  const actionButtonHover: CSSProperties = {
    backgroundColor: t.buttons.action.hover,
  };
  const actionButtonDisabled: CSSProperties = {
    backgroundColor: t.buttons.action.disabledBg,
    color: t.buttons.action.disabledFg,
    cursor: 'not-allowed',
  };

  const secondaryActionButton: CSSProperties = {
    backgroundColor: 'transparent',
    color: t.buttons.action.bg,
    border: `1px solid ${t.buttons.action.bg}`,
    borderRadius: t.radius.full,
    fontSize: '24px',
    fontWeight: t.typography.weights.regular,
    padding: '10px 40px',
    fontFamily: t.typography.fontFamily,
    cursor: 'pointer',
  };
  const secondaryActionButtonHover: CSSProperties = {
    backgroundColor: t.backgrounds.surfaceAlt,
  };

  return {
    container: { backgroundColor: t.backgrounds.app, color: t.text.primary, fontFamily: t.typography.fontFamily },

    actionButton,
    actionButtonHover,
    actionButtonDisabled,
    secondaryActionButton,
    secondaryActionButtonHover,
    card: {
      backgroundColor: t.backgrounds.app,
      border: `1px solid ${t.borders.default}`,
      borderRadius: t.radius.lg,
      padding: s.xl,
      boxShadow: t.shadow.sm,
    },
    section: { backgroundColor: "transparent", borderRadius: 0, padding: 0 },

    h1: { fontSize: t.typography.h1.size, fontWeight: t.typography.h1.weight, lineHeight: t.typography.h1.lineHeight, margin: `0 0 ${s.lg} 0` },
    h2: { fontSize: t.typography.h2.size, fontWeight: t.typography.h2.weight, lineHeight: t.typography.h2.lineHeight, margin: `0 0 ${s.md} 0` },
    h3: { fontSize: t.typography.h3.size, fontWeight: t.typography.h3.weight, lineHeight: t.typography.h3.lineHeight, margin: `0 0 ${s.sm} 0` },
    h4: { fontSize: t.typography.h4.size, fontWeight: t.typography.h4.weight, lineHeight: t.typography.h4.lineHeight, margin: `0 0 ${s.sm} 0` },
    p:  { fontSize: t.typography.body.size, fontWeight: t.typography.body.weight, lineHeight: t.typography.body.lineHeight, margin: `0 0 ${s.md} 0` },

    bannerCallout: { backgroundColor: t.brand.primary, borderRadius: t.radius.lg, padding: s["2xl"], boxShadow: t.shadow.md, color: t.text.onBrand },
    sectionTab: { backgroundColor: t.brand.primary, color: t.text.onBrand, fontWeight: 800, padding: "14px 22px", borderRadius: `0 0 0 ${t.radius.md}`, boxShadow: t.shadow.md },
    displayParagraph: {
      // mimic CSS clamp: do it in component via responsive calc if needed
      fontSize: t.typography.display.size,
      fontWeight: t.typography.display.weight,
      lineHeight: t.typography.display.lineHeight,
      color: t.text.muted,
    },

    mediaBlock: { position: "relative", borderRadius: t.radius.lg, overflow: "hidden", backgroundColor: t.backgrounds.inverse },
    mediaOverlay: { position: "absolute", left: s.xl, bottom: s.xl, color: t.text.onInverse, maxWidth: "70%" },
    playButton: {
      position: "absolute",
      inset: 0,
      display: "grid",
      placeItems: "center",
    },
    mediaLabel: { position: "absolute", top: 0, right: 0, backgroundColor: t.brand.primary, color: t.text.onBrand, fontWeight: 800, padding: "14px 24px", borderRadius: `0 0 0 ${t.radius.md}`, boxShadow: t.shadow.md },

    accordion: { backgroundColor: t.backgrounds.app, border: `1px solid ${t.borders.default}`, borderRadius: t.radius.lg, overflow: "hidden" },
    accordionItem: { borderTop: `1px solid ${t.borders.default}` },
    accordionTrigger: { width: "100%", background: "transparent", border: 0, padding: `18px 22px`, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", textAlign: "left" },
    accordionTitle: { fontWeight: 800, fontSize: "18px", color: t.text.primary },
    accordionIcon: { fontWeight: 900, fontSize: "28px", lineHeight: 1, color: t.text.primary },
    accordionPanel: { padding: `0 22px ${s.xl}`, color: t.text.muted },

    // Comparison slider
    comparisonContainer: {
      backgroundColor: "transparent",
      borderRadius: 0,
      padding: 0,
    },
    comparisonHeader: {
      marginBottom: s.lg,
    },
    comparisonTitle: {
      fontSize: t.typography.h2.size,
      fontWeight: t.typography.h2.weight,
      lineHeight: t.typography.h2.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: `0 0 ${s.sm} 0`,
      color: t.text.primary,
    },
    comparisonSubtitle: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: 0,
      color: t.text.primary,
    },
    comparisonContentPanel: {
      flex: 1,
      backgroundColor: "transparent",
      border: "none",
      borderRadius: 0,
      padding: 0,
      display: "flex",
      alignItems: "flex-start",
      gap: s.lg,
    },
    comparisonOptionTitle: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.weights.bold,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: `0 0 ${s.sm} 0`,
      color: t.text.primary,
    },
    comparisonOptionBody: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: 0,
      color: t.text.primary,
    },
    comparisonOptionImage: {
      maxWidth: "min(220px, 40%)",
      flexShrink: 0,
      borderRadius: t.radius.md,
      display: "block",
    },

    // Flip cards
    flipCardContainer: {
      position: "relative",
      padding: 0,
      backgroundColor: "transparent",
      borderRadius: 0,
    },
    flipCardLabel: {
      position: "absolute",
      top: 0,
      right: 0,
      backgroundColor: t.brand.primary,
      color: t.text.onBrand,
      fontWeight: 800,
      fontSize: "14px",
      padding: "14px 24px",
      borderRadius: `0 ${t.radius.lg} 0 ${t.radius.md}`,
      boxShadow: t.shadow.md,
    },
    flipCardHeader: {
      marginBottom: s.xl,
    },
    flipCardTitle: {
      fontSize: t.typography.h2.size,
      fontWeight: t.typography.h2.weight,
      lineHeight: t.typography.h2.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: `0 0 ${s.md} 0`,
      color: t.text.primary,
    },
    flipCardDescription: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: 0,
      color: t.text.primary,
    },
    flipCardGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: s.lg,
    },
    flipCardScene: {
      perspective: "1000px",
      aspectRatio: "1 / 1",
      cursor: "pointer",
      width: "100%",
      minWidth: 0,
    },
    flipCardInner: {
      width: "100%",
      height: "100%",
      position: "relative",
      transformStyle: "preserve-3d",
      transition: "transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)",
    },
    flipCardFace: {
      position: "absolute",
      width: "100%",
      height: "100%",
      backfaceVisibility: "hidden",
      backgroundColor: t.backgrounds.app,
      border: `1px solid ${t.borders.default}`,
      borderRadius: t.radius.md,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: s.lg,
      boxSizing: "border-box",
      boxShadow: t.shadow.sm,
    },
    flipCardFaceBack: {
      transform: "rotateY(180deg)",
    },
    flipCardText: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.h3.weight,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      lineHeight: t.typography.body.lineHeight,
      textAlign: "center",
    },
    flipCardTextBack: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      lineHeight: t.typography.body.lineHeight,
      textAlign: "center",
    },

    // Click Expand
    clickExpandContainer: {
      backgroundColor: "transparent",
      borderRadius: 0,
      padding: 0,
      fontFamily: t.typography.fontFamily,
    },
    clickExpandHeader: {
      marginBottom: "30px",
    },
    clickExpandTitle: {
      fontSize: t.typography.h2.size,
      fontWeight: t.typography.h2.weight,
      lineHeight: t.typography.h2.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: `0 0 ${s.sm} 0`,
      color: t.text.primary,
    },
    clickExpandDescription: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: 0,
      color: t.text.primary,
    },
    clickExpandAccordionList: {
      ...interactionCard(t),
      padding: "48px 62px 49px",
      display: "flex",
      flexDirection: "column" as const,
      gap: "37px",
    },
    clickExpandAccordionItem: {
      display: "flex",
      flexDirection: "column",
      paddingBottom: "37px",
      borderBottom: `1px solid ${t.borders.default}`,
    },
    clickExpandAccordionItemExpanded: {},
    clickExpandAccordionTrigger: {
      width: "100%",
      background: 'transparent',
      border: 0,
      borderRadius: 0,
      padding: 0,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      cursor: "pointer",
      textAlign: "left",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      transition: "background-color 150ms ease, color 150ms ease",
    },
    clickExpandAccordionTriggerHover: {
      background: 'transparent',
      opacity: 0.82,
    },
    clickExpandAccordionTriggerActive: {
      background: 'transparent',
      color: t.text.primary,
    },
    clickExpandAccordionTopicTitle: {
      fontWeight: t.typography.h3.weight,
      fontSize: t.typography.body.size,
      lineHeight: t.typography.body.lineHeight,
      color: "inherit",
    },
    clickExpandAccordionIcon: {
      fontWeight: 700,
      fontSize: "29px",
      lineHeight: 1,
      color: "inherit",
      flexShrink: 0,
      width: "29px",
      height: "29px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    clickExpandAccordionPanel: {
      overflow: "hidden",
      transition: "max-height 250ms ease-out, opacity 250ms ease-out",
    },
    clickExpandAccordionPanelContent: {
      padding: "37px 0 0",
      display: "flex",
      gap: "20px",
      alignItems: "center",
    },
    clickExpandAccordionImage: {
      maxWidth: "clamp(120px, 30%, 200px)",
      flexShrink: 0,
      borderRadius: t.radius.md,
    },
    clickExpandTabShell: {
      ...interactionCard(t),
      overflow: "hidden",
    },
    clickExpandTabBar: {
      display: "flex",
      gap: "0",
      marginBottom: "0",
      width: "100%",
      boxSizing: "border-box",
    },
    clickExpandTab: {
      flex: "1 1 0%",
      minWidth: "0",
      padding: "25px 16px",
      backgroundColor: t.buttons.action.bg,
      border: 0,
      borderRight: `1px solid ${t.borders.default}`,
      cursor: "pointer",
      fontFamily: t.typography.fontFamily,
      fontSize: t.typography.body.size,
      fontWeight: t.typography.h3.weight,
      color: t.text.onInverse,
      textAlign: "center" as const,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: s.sm,
      transition: "background-color 150ms ease, color 150ms ease",
    },
    clickExpandTabHover: {
      backgroundColor: t.buttons.action.hover,
    },
    clickExpandTabActive: {
      backgroundColor: t.backgrounds.cardAlt,
      color: t.text.primary,
      fontWeight: t.typography.h3.weight,
    },
    clickExpandTabSelectWrapper: {
      position: "relative" as const,
      width: "100%",
      marginBottom: s.md,
    },
    clickExpandTabSelect: {
      width: "100%",
      appearance: "none" as const,
      backgroundColor: t.backgrounds.app,
      border: `1px solid ${t.borders.default}`,
      borderRadius: t.radius.md,
      padding: `${s.lg} calc(${s.xl} + 20px) ${s.lg} ${s.lg}`,
      boxSizing: "border-box",
      boxShadow: t.shadow.sm,
      cursor: "pointer",
      fontFamily: t.typography.fontFamily,
      fontSize: t.typography.body.size,
      fontWeight: t.typography.h3.weight,
      lineHeight: t.typography.body.lineHeight,
      color: t.text.primary,
    },
    clickExpandTabSelectIcon: {
      position: "absolute" as const,
      top: "50%",
      right: s.lg,
      transform: "translateY(-50%)",
      pointerEvents: "none" as const,
      color: t.text.primary,
      width: "18px",
      height: "18px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    clickExpandTabContent: {
      backgroundColor: t.backgrounds.cardAlt,
      padding: "35px 62px",
      display: "flex",
      gap: "20px",
      alignItems: "flex-start",
    },
    clickExpandTabContentText: {
      flex: 1,
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    clickExpandTabContentImage: {
      maxWidth: "clamp(120px, 30%, 200px)",
      flexShrink: 0,
      borderRadius: t.radius.md,
    },
    clickExpandCheckmark: {
      color: t.text.primary,
      fontSize: "18px",
      fontWeight: 700,
      flexShrink: 0,
      width: "24px",
      height: "24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },

    // Likert scale
    likertContainer: {
      backgroundColor: "transparent",
      borderRadius: 0,
      padding: 0,
      width: "100%",
      maxWidth: "1108px",
      margin: "0 auto",
    },
    likertHeader: {
      marginBottom: "30px",
      width: "100%",
    },
    likertTitle: {
      fontSize: t.typography.h2.size,
      fontWeight: t.typography.h2.weight,
      lineHeight: t.typography.h2.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: `0 0 ${s.sm} 0`,
      color: t.text.primary,
    },
    likertDescription: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: 0,
      color: t.text.primary,
    },
    likertCard: {
      // radius.xl === "15px" — same rendered radius as the previous hardcoded value.
      ...interactionCard(t),
      padding: "75px 32px 62px",
      display: "flex",
      flexDirection: "column",
      gap: s.xl,
      width: "100%",
      boxSizing: "border-box",
    },
    likertMetaRow: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: s.sm,
    },
    likertMetaLabel: {
      fontSize: "12px",
      fontWeight: 700,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: t.text.muted,
      fontFamily: t.typography.fontFamily,
    },
    likertMetaText: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      color: t.text.muted,
      margin: 0,
    },
    likertOptionsScroller: {
      // Fluid scale fits its container (no overflow) — see grid below.
      width: "100%",
      overflowX: "visible",
      paddingBottom: s.xs,
      display: "flex",
      justifyContent: "center",
    },
    likertOptionsLayout: {
      width: "100%",
      margin: "0 auto",
    },
    likertOptionsGrid: {
      // Equal fluid columns; capped at the original desktop spacing via an
      // inline maxWidth in the renderer, then shrinks to fit narrower widths
      // rather than overflowing/clipping (AA-173).
      display: "grid",
      columnGap: 0,
      rowGap: "24px",
      alignItems: "flex-start",
      width: "100%",
      margin: "0 auto",
    },
    likertAxisRow: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
      columnGap: s.lg,
      alignItems: "start",
      width: "100%",
    },
    likertAxisLabel: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "1.4",
      color: t.text.primary,
      fontFamily: t.typography.fontFamily,
      textAlign: "center" as const,
      // Fills its column and wraps — no fixed width to overhang/clip at the ends.
      width: "100%",
    },
    likertOptionButton: {
      // Fills its fluid grid column; the 49px dot inside stays centered.
      width: "100%",
      appearance: "none" as const,
      padding: 0,
      borderRadius: t.radius.md,
      border: 0,
      backgroundColor: "transparent",
      boxShadow: "none",
      color: t.text.primary,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      fontFamily: t.typography.fontFamily,
      cursor: "pointer",
      transition: `transform ${t.ui.transitionDefault}, opacity ${t.ui.transitionDefault}`,
      flex: "0 0 auto",
      outline: "none",
      position: "relative",
      WebkitTapHighlightColor: "transparent",
    },
    likertOptionButtonHover: {
      transform: "translateY(0)",
    },
    likertOptionButtonSelected: {
      transform: "translateY(0)",
    },
    likertOptionNumber: {
      width: "49px",
      height: "49px",
      borderRadius: t.radius.full,
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: t.borders.default,
      backgroundColor: t.backgrounds.app,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      transition: `background-color ${t.ui.transitionDefault}, border-color ${t.ui.transitionDefault}, box-shadow ${t.ui.transitionDefault}`,
    },
    likertOptionNumberSelected: {
      boxShadow: "none",
    },
    likertOptionTooltip: {
      color: t.text.muted,
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "1.35",
      fontFamily: t.typography.fontFamily,
      textAlign: "center" as const,
    },
    likertOptionTooltipCaret: {
      position: "absolute",
      left: "50%",
      bottom: "calc(100% + 8px)",
      width: "9px",
      height: "9px",
      borderLeft: `1px solid ${t.text.muted}`,
      borderTop: `1px solid ${t.text.muted}`,
      transform: "translateX(-50%) rotate(45deg)",
      boxSizing: "border-box" as const,
    },
    likertOptionLabelCell: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start",
      overflow: "visible" as const,
      position: "relative" as const,
    },
    likertOptionLabel: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "1.35",
      color: t.text.primary,
      whiteSpace: "normal",
      overflowWrap: "break-word",
      wordBreak: "normal",
      transition: `opacity ${t.ui.transitionDefault}`,
    },
    likertOptionLabelSelected: {
      color: t.text.primary,
      opacity: 1,
    },
    likertSubmitButton: {
      ...actionButton,
      margin: `${s.xl} auto 0`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "fit-content",
      borderRadius: "30px",
      boxShadow: "none",
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      minWidth: "114px",
      transition: `background-color ${t.ui.transitionDefault}, color ${t.ui.transitionDefault}, opacity ${t.ui.transitionDefault}`,
    },
    likertSubmitButtonDisabled: {
      ...actionButtonDisabled,
      boxShadow: "none",
    },
    likertResultsAnswerRow: {
      display: "flex",
      alignItems: "center",
      gap: "22px",
      flexWrap: "wrap",
      marginBottom: "21px",
    },
    likertResultsAnswerLabel: {
      fontSize: "18px",
      fontWeight: t.typography.body.weight,
      lineHeight: "1.4",
      fontFamily: t.typography.fontFamily,
      fontStyle: "italic",
      color: t.text.primary,
    },
    likertResultsAnswerBadge: {
      minHeight: "46px",
      minWidth: "147px",
      padding: "5px 16px",
      borderRadius: "5px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "18px",
      fontWeight: t.typography.h3.weight,
      lineHeight: "1.2",
      fontFamily: t.typography.fontFamily,
      color: t.text.onBrand,
      textAlign: "center" as const,
      boxSizing: "border-box",
    },
    likertResultsCard: {
      // radius.xl === "15px" — same rendered radius as the previous hardcoded value.
      ...interactionCard(t),
      padding: "28px 20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: "21px",
      width: "100%",
      boxSizing: "border-box",
    },
    likertResultsDistribution: {
      display: "flex",
      flexDirection: "column",
      gap: "13px",
      width: "100%",
    },
    likertResultsDistributionHeader: {
      display: "flex",
      alignItems: "baseline",
      gap: s.sm,
      flexWrap: "wrap",
      padding: `0 ${s.xs}`,
    },
    likertResultsDistributionTitle: {
      fontSize: "18px",
      fontWeight: t.typography.h3.weight,
      lineHeight: "1.2",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    likertResultsDistributionResponses: {
      fontSize: "18px",
      fontWeight: t.typography.body.weight,
      lineHeight: "1.2",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    likertResultsChartSurface: {
      backgroundColor: t.backgrounds.app,
      borderRadius: "5px",
      padding: "34px 20px",
      width: "100%",
      boxSizing: "border-box",
    },
    likertResultsChartTrack: {
      position: "relative" as const,
      width: "100%",
      height: "46px",
    },
    likertResultsChartSegment: {
      position: "absolute" as const,
      top: 0,
      height: "46px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0 10px",
      boxSizing: "border-box",
      overflow: "hidden",
    },
    likertResultsChartSegmentLabel: {
      fontSize: "18px",
      fontWeight: t.typography.body.weight,
      lineHeight: "1.2",
      fontFamily: t.typography.fontFamily,
      color: t.text.onBrand,
      whiteSpace: "nowrap" as const,
    },
    likertResultsAxisRow: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
      alignItems: "start",
      gap: s.sm,
      width: "100%",
      paddingTop: "12px",
      boxSizing: "border-box",
    },
    likertResultsAxisLabel: {
      fontSize: "18px",
      fontWeight: t.typography.body.weight,
      lineHeight: "1.2",
      fontFamily: t.typography.fontFamily,
      fontStyle: "italic",
      color: t.text.primary,
      whiteSpace: "normal" as const,
      overflowWrap: "anywhere",
    },
    likertResultsTabBar: {
      display: "flex",
      gap: "2px",
      alignItems: "flex-end",
      width: "100%",
    },
    likertResultsTab: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "58px",
      backgroundColor: t.buttons.action.bg,
      border: "none",
      padding: "0 16px",
      cursor: "pointer",
      fontSize: "20px",
      fontWeight: t.typography.body.weight,
      lineHeight: "1.2",
      fontFamily: t.typography.fontFamily,
      color: t.text.onInverse,
      boxSizing: "border-box" as const,
    },
    likertResultsTabActive: {
      backgroundColor: t.backgrounds.cardAlt,
      color: t.text.primary,
      cursor: "default",
    },
    likertResultsTabContent: {
      padding: "28px 20px 24px",
      display: "flex",
      flexDirection: "column" as const,
      gap: "21px",
      width: "100%",
      boxSizing: "border-box" as const,
    },
    likertResultsTable: {
      width: "100%",
      tableLayout: "fixed" as const,
      borderCollapse: "collapse" as const,
      backgroundColor: t.backgrounds.app,
      borderRadius: "5px",
      fontSize: "16px",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    likertResultsTableHeader: {
      borderBottom: `1px solid ${t.borders.default}`,
    },
    likertResultsTableHeaderCell: {
      padding: "12px 8px",
      fontWeight: t.typography.body.weight,
      fontSize: "16px",
      lineHeight: "1.2",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      textAlign: "center" as const,
      verticalAlign: "bottom" as const,
      borderLeft: `1px solid ${t.borders.default}`,
    },
    likertResultsTableRow: {
      borderBottom: `1px solid ${t.borders.default}`,
    },
    likertResultsTableCell: {
      padding: "12px 8px",
      fontWeight: t.typography.body.weight,
      fontSize: "18px",
      lineHeight: "1.2",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      textAlign: "center" as const,
      verticalAlign: "middle" as const,
      borderLeft: `1px solid ${t.borders.default}`,
    },
    likertResultsTableRowLabel: {
      padding: "12px 16px",
      fontWeight: t.typography.body.weight,
      fontSize: "18px",
      lineHeight: "1.4",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      textAlign: "left" as const,
      verticalAlign: "middle" as const,
    },

    // Likert scale — small-mobile (≤576px) reflow (AA-173).
    // Question options become full-width stacked radio rows; the results
    // Summary/Detail/Table selector becomes a dropdown (see renderer).
    likertMobileCard: {
      ...interactionCard(t),
      padding: "30px 16px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      boxSizing: "border-box",
    },
    likertMobileOptionsList: {
      display: "flex",
      flexDirection: "column",
      gap: s.lg,
      alignItems: "stretch",
      width: "100%",
    },
    likertMobileOptionRow: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      width: "100%",
      minHeight: "50px",
      // Longhand padding/border — inline-style border shorthand can clash with
      // DnD props elsewhere; box-sizing keeps the explicit min-height honest.
      paddingTop: "10px",
      paddingBottom: "10px",
      paddingLeft: "14px",
      paddingRight: "14px",
      backgroundColor: t.backgrounds.app,
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: t.borders.interactive,
      // radius.xs (4px) — matches Single Choice option boxes; Figma's 5px is
      // within snap tolerance.
      borderRadius: t.radius.xs,
      // Same lift as Single Choice option boxes.
      boxShadow: t.shadow.lg,
      cursor: "pointer",
      textAlign: "left" as const,
      appearance: "none" as const,
      fontFamily: t.typography.fontFamily,
      boxSizing: "border-box" as const,
      transition: `border-color ${t.ui.transitionDefault}, background-color ${t.ui.transitionDefault}`,
      WebkitTapHighlightColor: "transparent",
    },
    likertMobileOptionRowHover: {
      // Reuses the shared selectable-option hover (Single Choice / Quiz):
      // accent border + light-blue fill. A clearer box-level cue than the dot.
      borderColor: t.choice.hover.border,
      backgroundColor: t.choice.hover.bg,
    },
    likertMobileOptionRowSelected: {
      // Pronounced chosen state: persistent 2px accent border + light-blue fill.
      // Paired with the filled circle and bold label below.
      borderWidth: "2px",
      borderColor: t.choice.hover.border,
      backgroundColor: t.choice.hover.bg,
    },
    likertMobileRadio: {
      width: "29px",
      height: "29px",
      borderRadius: t.radius.full,
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: t.borders.interactive,
      backgroundColor: t.backgrounds.app,
      flexShrink: 0,
      boxSizing: "border-box" as const,
      transition: `background-color ${t.ui.transitionDefault}, border-color ${t.ui.transitionDefault}`,
    },
    likertMobileRadioSelected: {
      // NA-242: selection state uses the fixed UI accent — never the account accent.
      backgroundColor: t.ui.fixedAccent,
      borderColor: t.ui.fixedAccent,
    },
    likertMobileOptionLabel: {
      fontSize: "18px",
      fontWeight: t.typography.body.weight,
      lineHeight: "1.2",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    likertMobileOptionLabelSelected: {
      // Spec body font is 400/700, but full bold reads too heavy here — step
      // to medium (500) for a softer selected emphasis.
      fontWeight: t.typography.weights.medium,
    },
    likertResultsSelectWrap: {
      // Padded inset so the shared Dropdown sits inside the results card.
      width: "100%",
      padding: "20px 20px 0",
      boxSizing: "border-box" as const,
    },
    // Small-mobile Detail view: stacked per-option rows (label + % over a thin
    // bar), replacing the segmented bar which crowds on narrow widths (AA-173).
    likertDetailRowsList: {
      display: "flex",
      flexDirection: "column",
      gap: "15px",
      width: "100%",
    },
    likertDetailRow: {
      display: "flex",
      flexDirection: "column",
      gap: "5px",
      width: "100%",
    },
    likertDetailRowHeader: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: s.md,
      width: "100%",
    },
    likertDetailRowLabel: {
      fontSize: t.typography.sizes.sm,
      fontWeight: t.typography.body.weight,
      lineHeight: "1.2",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    likertDetailRowPercent: {
      fontSize: t.typography.sizes.sm,
      fontWeight: t.typography.body.weight,
      lineHeight: "1.2",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      flexShrink: 0,
    },
    likertDetailTrack: {
      width: "100%",
      height: "6px",
      borderRadius: t.radius.full,
      backgroundColor: t.borders.default,
      overflow: "hidden",
    },
    likertDetailFill: {
      height: "6px",
      borderRadius: t.radius.full,
    },
    // Small-mobile Table view: transposed to match the Figma spec — scale
    // options as rows, distributions as columns, 14px centered body copy.
    likertTableMobile: {
      width: "100%",
      tableLayout: "fixed" as const,
      borderCollapse: "collapse" as const,
      backgroundColor: t.backgrounds.app,
      borderRadius: t.radius.xs,
      overflow: "hidden",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    likertTableMobileHeaderCell: {
      padding: `${s.md} ${s.sm}`,
      fontSize: t.typography.sizes.sm,
      fontWeight: t.typography.body.weight,
      lineHeight: "1.3",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      textAlign: "center" as const,
      verticalAlign: "bottom" as const,
      borderLeft: `1px solid ${t.borders.default}`,
      borderBottom: `1px solid ${t.borders.default}`,
    },
    likertTableMobileCornerCell: {
      borderBottom: `1px solid ${t.borders.default}`,
    },
    likertTableMobileRowLabel: {
      padding: `${s.md} ${s.sm}`,
      fontSize: t.typography.sizes.sm,
      fontWeight: t.typography.body.weight,
      lineHeight: "1.3",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      textAlign: "center" as const,
      verticalAlign: "middle" as const,
      borderBottom: `1px solid ${t.borders.default}`,
    },
    likertTableMobileCell: {
      padding: `${s.md} ${s.sm}`,
      fontSize: t.typography.sizes.sm,
      fontWeight: t.typography.body.weight,
      lineHeight: "1.3",
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      textAlign: "center" as const,
      verticalAlign: "middle" as const,
      borderLeft: `1px solid ${t.borders.default}`,
      borderBottom: `1px solid ${t.borders.default}`,
    },

    // Workplace Color Spectrum
    workplaceSpectrumContainer: {
      backgroundColor: "transparent",
      borderRadius: 0,
      padding: 0,
      width: "100%",
    },
    workplaceSpectrumHeader: {
      marginBottom: "30px",
    },
    workplaceSpectrumTitle: {
      fontSize: t.typography.h2.size,
      fontWeight: t.typography.h2.weight,
      lineHeight: t.typography.h2.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: `0 0 ${s.sm} 0`,
      color: t.text.primary,
    },
    workplaceSpectrumSubtitle: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: 0,
      color: t.text.primary,
    },
    workplaceSpectrumCard: {
      ...interactionCard(t),
      borderRadius: t.radius.lg, // tighter radius than the shared card base
      padding: `${s.lg} ${s.xl}`,
      display: "flex",
      flexDirection: "column",
      gap: 0,
    },
    workplaceSpectrumGaugeArea: {
      position: "relative",
      width: "100%",
      maxWidth: "620px",
      margin: "0 auto",
      paddingTop: s.lg,
    },
    workplaceSpectrumGaugeSvg: {
      width: "100%",
      height: "auto",
      display: "block",
    },
    workplaceSpectrumSegment: {
      cursor: "pointer",
      transition: `filter ${t.ui.transitionDefault}`,
    },
    workplaceSpectrumLabelButton: {
      position: "absolute" as const,
      background: "transparent",
      border: 0,
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: s.xs,
      cursor: "pointer",
      whiteSpace: "nowrap" as const,
      color: t.text.primary,
      transition: `opacity ${t.ui.transitionDefault}`,
    },
    workplaceSpectrumLabelButtonMuted: {
      opacity: 0.5,
    },
    workplaceSpectrumLabelTitle: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.h3.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
    },
    workplaceSpectrumLabelSubtitle: {
      fontSize: "18px",
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
    },
    workplaceSpectrumDefinitionArea: {
      margin: `0 auto`,
      maxWidth: "62ch",
      minHeight: "110px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      padding: "0 0 30px 0",
    },
    workplaceSpectrumDefinition: {
      margin: 0,
      maxWidth: "62ch",
      textAlign: "center" as const,
      fontSize: "18px",
      lineHeight: "1.4",
      fontWeight: t.typography.body.weight,
      fontStyle: "italic",
      color: t.text.primary,
    },
    workplaceSpectrumSubmitButton: {
      ...actionButton,
      margin: `${s.lg} auto 0`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "fit-content",
      borderRadius: t.radius.full,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      minWidth: "148px",
      transition: `background-color ${t.ui.transitionDefault}, color ${t.ui.transitionDefault}`,
    },
    workplaceSpectrumSubmitButtonDisabled: actionButtonDisabled,
    workplaceSpectrumResultsHeaderRow: {
      display: "flex",
      alignItems: "center",
      gap: "22px",
      flexWrap: "wrap",
    },
    workplaceSpectrumResultsYourAnswerLabel: {
      fontSize: t.typography.sizes.ui,
      fontStyle: "italic",
      fontWeight: t.typography.weights.regular,
      lineHeight: t.typography.lineHeights.uiSoft,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    workplaceSpectrumResultsYourAnswerPill: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "5px 16px",
      borderRadius: "5px",
      minWidth: "147px",
      minHeight: "46px",
      boxSizing: "border-box",
    },
    workplaceSpectrumResultsYourAnswerPillText: {
      fontSize: t.typography.sizes.ui,
      fontWeight: t.typography.weights.bold,
      lineHeight: t.typography.lineHeights.tight,
      fontFamily: t.typography.fontFamily,
      color: t.text.onBrand,
      textAlign: "center" as const,
    },
    workplaceSpectrumResultsYourAnswerPillTextDark: {
      color: t.text.primary,
    },
    workplaceSpectrumResultsYourAnswerPillBordered: {
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "#222442",
    },
    workplaceSpectrumResultsCard: {
      ...interactionCard(t),
      backgroundColor: t.backgrounds.resultsPanel,
      paddingTop: s["4xl"],
      paddingBottom: s["4xl"],
      paddingInline: "67px",
      display: "flex",
      flexDirection: "column",
      gap: s["3xl"],
      boxSizing: "border-box",
    },
    workplaceSpectrumResultsCorrectAnswerCluster: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "flex-start",
      gap: s["3xl"],
    },
    workplaceSpectrumResultsChartsRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: s.md,
      justifyContent: "center",
    },
    workplaceSpectrumResultsAnswerTile: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: s.md,
      width: "238px",
      flexShrink: 0,
    },
    workplaceSpectrumResultsAnswerBar: {
      position: "relative",
      width: "238px",
      height: "61px",
      borderRadius: t.radius.xs,
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "#222442",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "visible",
      boxSizing: "border-box",
    },
    workplaceSpectrumResultsEmoji: {
      position: "absolute",
      top: "-11px",
      left: "50%",
      transform: "translateX(-50%)",
      width: "82px",
      height: "82px",
      objectFit: "contain" as const,
    },
    workplaceSpectrumResultsAnswerLabel: {
      fontSize: t.typography.sizes.md,
      fontWeight: t.typography.weights.bold,
      lineHeight: t.typography.lineHeights.normal,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      textAlign: "center" as const,
    },
    workplaceSpectrumResultsExplanation: {
      flex: "1 1 260px",
      textAlign: "left" as const,
      fontSize: t.typography.sizes.md,
      lineHeight: t.typography.lineHeights.normal,
      fontWeight: t.typography.weights.regular,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    workplaceSpectrumResultsChartCard: {
      backgroundColor: t.backgrounds.app,
      borderRadius: t.radius.xs,
      flex: "1 1 300px",
      minWidth: "260px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      paddingTop: s.xl,
      paddingBottom: s.xl,
      gap: 0,
      boxSizing: "border-box",
    },
    workplaceSpectrumResultsChartHeader: {
      display: "flex",
      gap: s.sm,
      alignItems: "baseline",
      flexWrap: "wrap",
      justifyContent: "center",
    },
    workplaceSpectrumResultsChartTitle: {
      fontSize: t.typography.sizes.ui,
      fontWeight: t.typography.weights.bold,
      lineHeight: t.typography.lineHeights.normal,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      margin: 0,
    },
    workplaceSpectrumResultsChartResponses: {
      fontSize: t.typography.sizes.ui,
      fontWeight: t.typography.weights.regular,
      lineHeight: t.typography.lineHeights.normal,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      margin: 0,
    },
    workplaceSpectrumResultsChartSvg: {
      width: "100%",
      maxWidth: "371px",
      height: "auto",
      display: "block",
    },
    workplaceSpectrumResultsLegend: {
      width: "100%",
      maxWidth: "371px",
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: s.xl,
    },
    workplaceSpectrumResultsLegendItem: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center" as const,
    },
    workplaceSpectrumResultsLegendLabel: {
      fontSize: t.typography.sizes.ui,
      fontWeight: t.typography.weights.regular,
      lineHeight: t.typography.lineHeights.normal,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    workplaceSpectrumResultsLegendValue: {
      fontSize: t.typography.sizes.md,
      fontWeight: t.typography.weights.bold,
      lineHeight: t.typography.lineHeights.normal,
      fontFamily: t.typography.fontFamily,
    },

    // FAQ Accordion
    faqAccordionContainer: {
      backgroundColor: "transparent",
      borderRadius: 0,
      padding: 0,
    },
    faqAccordionHeader: {
      marginBottom: s.lg,
    },
    faqAccordionTitle: {
      fontSize: t.typography.h2.size,
      fontWeight: t.typography.h2.weight,
      lineHeight: t.typography.h2.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: `0 0 ${s.sm} 0`,
      color: t.text.primary,
    },
    faqAccordionSubtitle: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      margin: 0,
      color: t.text.primary,
    },
    faqAccordionItemsList: {
      display: "flex",
      flexDirection: "column",
      gap: s.md,
    },
    faqAccordionItem: {
      display: "flex",
      flexDirection: "column",
    },
    faqAccordionQuestionRow: {
      display: "flex",
      alignItems: "flex-start",
      gap: s.md,
    },
    faqAccordionQuestionBox: {
      backgroundColor: t.backgrounds.app,
      border: `1px solid ${t.borders.default}`,
      borderRadius: t.radius.md,
      padding: `${s.lg} ${s.xl}`,
      display: "flex",
      alignItems: "center",
      gap: s.md,
      cursor: "pointer",
      boxShadow: t.shadow.sm,
      flex: 1,
    },
    faqAccordionPersonIcon: {
      flexShrink: 0,
      width: "24px",
      height: "24px",
      padding: s.xs,
      borderRadius: t.radius.full,
      backgroundColor: t.backgrounds.surface,
      color: t.text.muted,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    faqAccordionQuestionText: {
      flex: 1,
      fontSize: t.typography.body.size,
      fontWeight: t.typography.h3.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    faqAccordionChevron: {
      flexShrink: 0,
      width: "20px",
      height: "20px",
      color: t.text.muted,
      transition: "transform 200ms ease-out",
      transform: "rotate(0deg)",
    },
    faqAccordionChevronExpanded: {
      transform: "rotate(180deg)",
    },
    faqAccordionAnswerWrapper: {
      overflow: "hidden",
      transition: "max-height 200ms ease-out, opacity 200ms ease-out",
    },
    faqAccordionAnswerRow: {
      display: "flex",
      alignItems: "flex-start",
      gap: s.md,
      marginTop: s.md,
    },
    faqAccordionAnswerBox: {
      flex: 1,
      backgroundColor: t.backgrounds.inverse,
      borderRadius: t.radius.md,
      padding: s.lg,
    },
    faqAccordionAnswerText: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      color: t.text.onInverse,
    },
    faqAccordionLightbulbIcon: {
      flexShrink: 0,
      width: "24px",
      height: "24px",
      padding: s.xs,
      borderRadius: t.radius.full,
      backgroundColor: t.backgrounds.surface, // Light gray circle background
      color: t.text.muted, // Muted icon color
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },

    // Policy Acknowledgement
    policyContainer: {
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      width: "80%",
      maxWidth: "100%",
      margin: "0 auto",
      backgroundColor: "transparent",
      borderRadius: 0,
      padding: 0,
      boxSizing: "border-box" as const,
    },
    policyTitle: {
      fontSize: t.typography.h2.size,
      fontWeight: t.typography.h2.weight,
      lineHeight: t.typography.h2.lineHeight,
      margin: 0,
    },
    policyDescription: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      margin: `${s.sm} 0 0 0`,
    },
    policyButtonRow: {
      display: "flex",
      alignItems: "center",
      gap: s["2xl"],
      flexWrap: "wrap" as const,
      marginTop: "30px",
    },
    policyViewButton: {
      ...actionButton,
      display: "inline-flex",
      alignItems: "center",
      gap: "10px",
      borderRadius: "30px",
      fontWeight: t.typography.body.weight,
      lineHeight: "normal",
      transition: `background-color ${t.ui.transitionDefault}`,
    },
    policyViewButtonHover: actionButtonHover,
    policyViewButtonIcon: {
      width: "18px",
      height: "24px",
      flexShrink: 0,
    },
    policyCheckboxWrapper: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    policyCheckboxInput: {
      width: "22px",
      height: "22px",
      border: `1px solid ${t.buttons.action.bg}`,
      backgroundColor: "#D9D9D9",
      cursor: "pointer",
      margin: 0,
      accentColor: t.brand.primary,
    },
    policyCheckboxLabel: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: "normal",
      cursor: "pointer",
    },
    policyAcknowledgedWrapper: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    policyAcknowledgedIcon: {
      width: "20px",
      height: "20px",
      color: t.text.primary,
    },
    policyAcknowledgedText: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: "normal",
    },
    policyHelperText: {
      fontSize: "18px",
      fontStyle: "italic",
      fontWeight: 400,
      lineHeight: "normal",
      margin: `${s.md} 0 0 0`,
      color: t.text.primary,
    },
    policyAcknowledgedMessage: {
      margin: "30px 0 0",
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      textAlign: "center" as const,
    },
    policyModalOverlay: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    },
    policyModalContent: {
      backgroundColor: "#FFFFFF",
      borderRadius: t.radius.lg,
      width: "90vw",
      height: "85vh",
      maxWidth: "1000px",
      display: "flex",
      flexDirection: "column" as const,
      overflow: "hidden",
      position: "relative" as const,
    },
    policyModalHeader: {
      display: "flex",
      alignItems: "center",
      padding: `${s.lg} ${s.xl}`,
      borderBottom: `1px solid ${t.borders.default}`,
      flexShrink: 0,
    },
    policyModalHeaderTitle: {
      fontSize: t.typography.body.size,
      fontWeight: 700,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    policyModalCloseButton: {
      position: "absolute" as const,
      top: "12px",
      right: "12px",
      width: "36px",
      height: "36px",
      border: "none",
      borderRadius: "50%",
      backgroundColor: "rgba(0, 0, 0, 0.08)",
      color: t.text.primary,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
      zIndex: 1,
    },
    policyModalIframe: {
      flex: 1,
      border: "none",
      width: "100%",
    },
    policyModalFooter: {
      display: "flex",
      justifyContent: "center",
      padding: `${s.lg} ${s.xl}`,
      borderTop: `1px solid ${t.borders.default}`,
      flexShrink: 0,
    },
    policyModalDoneButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      backgroundColor: "#23242B",
      color: "#FFFFFF",
      border: "none",
      borderRadius: "30px",
      padding: "12px 48px",
      fontSize: t.typography.body.size,
      fontWeight: 600,
      fontFamily: t.typography.fontFamily,
      cursor: "pointer",
      lineHeight: "normal",
    },

    // Electronic Signature
    esigContainer: {
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      width: "80%",
      maxWidth: "100%",
      margin: "0 auto",
      backgroundColor: "transparent",
      borderRadius: 0,
      padding: 0,
      boxSizing: "border-box" as const,
    },
    esigTitle: {
      fontSize: t.typography.h1.size,
      fontWeight: t.typography.h1.weight,
      lineHeight: t.typography.h1.lineHeight,
      margin: 0,
    },
    esigDescription: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      margin: `${s.sm} 0 0 0`,
    },
    esigCheckboxContainer: {
      // cardAlt (#F1F1F1) / 3px app (#FFFFFF) / radius.xl (15px) / shadow.lg —
      // identical rendered values to the previously hardcoded hexes.
      ...interactionCard(t),
      display: "flex",
      alignItems: "center",
      gap: "13px",
      padding: "23px 52px",
      marginTop: "25px",
      position: "relative" as const,
      width: "fit-content",
    },
    esigCheckboxInput: {
      width: "22px",
      height: "22px",
      border: `1px solid ${t.buttons.action.bg}`,
      // #D9D9D9 awaits a dedicated token — the only exact-value token today is
      // buttons.action.disabledBg (disabled-button semantics, not a checkbox bg).
      backgroundColor: "#D9D9D9",
      cursor: "pointer",
      margin: 0,
      accentColor: t.brand.primary,
    },
    esigCheckboxLabel: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: "normal",
      cursor: "pointer",
    },

    linkContainer: {
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
      width: "80%",
      maxWidth: "100%",
      margin: "0 auto",
      backgroundColor: "transparent",
      borderRadius: 0,
      padding: 0,
      boxSizing: "border-box" as const,
    },
    linkTitle: {
      fontSize: t.typography.h2.size,
      fontWeight: t.typography.h2.weight,
      lineHeight: t.typography.h2.lineHeight,
      margin: 0,
    },
    linkDescription: {
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      margin: `${s.sm} 0 0 0`,
    },
    linkRow: {
      marginTop: "30px",
    },
    linkAnchor: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: 0,
      border: "none",
      background: "none",
      cursor: "pointer",
      color: t.text.link,
      textDecoration: "underline",
      fontSize: t.typography.body.size,
      fontWeight: t.typography.body.weight,
      lineHeight: t.typography.body.lineHeight,
      fontFamily: t.typography.fontFamily,
    },
    linkAnchorHover: {
      color: t.text.link,
      opacity: 0.8,
    },
    linkAnchorIcon: {
      width: "20px",
      height: "20px",
      flexShrink: 0,
    },
  };
}
